import CardActions from "src/pages/sistema/components/CardActions";
import {useCuentas} from "../hooks/useCuentas";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {EntityWithIdFields, QueryStatus} from "src/types";
import {OperacionCorporateCuentaFields} from "../types/fields";
import {useState} from "react";
import {ICON_MAS} from "src/pages/sistema/components/SharedIcons";
import {DialogNuevoDetalle} from "./DialogNuevoDetalle";
import {ITableColumn, TableList} from "src/components/table";

import {CuentaCorporate, CuentaCorporateDetalle} from "../types/types";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {HttpCorporate} from "../api/httpCorporate";
import toast from "react-hot-toast";
import {useConfirmationDialog} from "src/hooks/useConfirmationDialog";
import {numberFormatter} from "src/utils/formatters/numberFormatter";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {AsyncSelect} from "src/components/forms";
import {HttpCacheOperacion} from "src/pages/sistema/api/httpCacheOperacion";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {OperacionCorporateImporteFields} from "src/pages/sistema/types/importes";

type ImporteFieldProps = {
  importe: number
  permiteEditar: boolean
  codMoneda: number
  simboloMoneda: string
  importeTipo: 'concertacion' | 'dpv' | 'fop'
  idOperacion: number
  idCuenta: number
  idCuentaDetalle: number
  refetch: () => void
}

/**
 * Una celda de la tabla de detalles. Maneja el estado de su celda (editable y eso).
 */
const ImporteField = ({
                        importe,
                        importeTipo,
                        permiteEditar,
                        codMoneda,
                        simboloMoneda,
                        idOperacion,
                        idCuenta,
                        idCuentaDetalle,
                        refetch
                      }: ImporteFieldProps) => {

  const [editable, setEditable] = useState<boolean>(false);

  const {control, handleSubmit} = useForm<FormActualizarImportes>({
    resolver: zodResolver(schemaActualizarImportes),
    defaultValues: {
      ...(importeTipo === 'concertacion' ? {[OperacionCorporateCuentaFields.ImporteConcertacion]: importe} : {}),
      ...(importeTipo === 'dpv' ? {[OperacionCorporateCuentaFields.ImporteDPV]: importe} : {}),
      ...(importeTipo === 'fop' ? {[OperacionCorporateCuentaFields.ImporteFOP]: importe} : {}),
    }
  });

  const onUpdateImporte: SubmitHandler<FormActualizarImportes> = (data) => {
    const promise = HttpCorporate.putCuentaDetalle(idOperacion, idCuenta, idCuentaDetalle, data)
    toast.promise(promise, {
      loading: 'Actualizando importe.',
      success: 'Actualizado correctamente.',
      error: e => `${e}`
    })
      .then(() => {
        setEditable(false)
        refetch()
      })
  }

  return (
    <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='nowrap'>
      {
        (permiteEditar && editable) ?
          <AsyncSelect
            control={control}
            name={
              importeTipo === 'concertacion' ? OperacionCorporateCuentaFields.ImporteConcertacion :
                importeTipo === 'dpv' ? OperacionCorporateCuentaFields.ImporteDPV :
                  importeTipo === 'fop' ? OperacionCorporateCuentaFields.ImporteFOP : ''
            }
            label='Importe'
            loadOptions={() => HttpCacheOperacion.getImportesCorporatePorMoneda(codMoneda)}
            valueField={OperacionCorporateImporteFields.CorporateImporte}
          />
          :
          <Typography noWrap variant='body2'>
            {numberFormatter.toStringWithAmount(importe, simboloMoneda)}
          </Typography>
      }
      {
        permiteEditar &&
        (
          editable ?
            <Box display='flex' justifyContent='center' flexWrap='nowrap'>
              <Tooltip title='Cancelar'>
                <IconButton onClick={() => setEditable(false)}>
                  <CancelIcon color='error'/>
                </IconButton>
              </Tooltip>
              <Tooltip title='Confirmar'>
                <IconButton onClick={handleSubmit(onUpdateImporte)}>
                  <CheckCircleIcon color='success'/>
                </IconButton>
              </Tooltip>
            </Box>
            :
            <Tooltip title='Editar'>
              <IconButton onClick={() => setEditable(true)}>
                <EditIcon color='info'/>
              </IconButton>
            </Tooltip>
        )
      }
    </Box>
  )
}

const schemaActualizarImportes = z.object({
  [OperacionCorporateCuentaFields.ImporteConcertacion]: z.number().optional(),
  [OperacionCorporateCuentaFields.ImporteDPV]: z.number().optional(),
  [OperacionCorporateCuentaFields.ImporteFOP]: z.number().optional()
})

type FormActualizarImportes = z.infer<typeof schemaActualizarImportes>

/**
 * Un detalle de una cuenta, es decir, una fila de la tabla de detalles.
 */
// const CorporateCuentaDetalle = ({ detalle }: { detalle: DetalleCorporateDetalle }) => {

//     const { control, handleSubmit, watch } = useForm<FormActualizarImportes>({
//         resolver: zodResolver(schemaActualizarImportes),
//         // defaultValues: {
//         //     [OperacionCorporateDetalleFields.ImporteConcertacion]:
//         // }
//     });

//     return (

//     )
// }

const getColumnasParaUnaCuenta = (idOperacion: number, permiteEditar: boolean, idCuenta: number, codMoneda: number, simboloMoneda: string, refetch: () => void) => {

  const columnsTablaCorporateCuenta: ITableColumn[] = [
    {
      label: 'Sector comercial',
      width: '40%',
      value: OperacionCorporateCuentaFields.Sector,
      onRenderCell: (entity: CuentaCorporateDetalle) => {
        let estilo = {}
        try {
          estilo = JSON.parse(entity[OperacionCorporateCuentaFields.Estilo] || '{}');
        } catch (error) {
          console.error("No se pudo parsear el JSON de estilo:", entity[OperacionCorporateCuentaFields.Estilo]);
        }

        return (
          <Typography noWrap variant='body2'
                      sx={{
                        ...estilo,
                      }}
          >
            {entity[OperacionCorporateCuentaFields.Sector]}
          </Typography>
        )
      }
    },
    {
      label: 'Concertación',
      value: OperacionCorporateCuentaFields.ImporteConcertacion,
      width: '20%',
      onRenderCell: (entity: CuentaCorporateDetalle) => {
        return (
          <ImporteField
            importe={entity.importe_Concertacion}
            permiteEditar={entity.permiteModificar && permiteEditar}
            codMoneda={codMoneda}
            simboloMoneda={simboloMoneda}
            idOperacion={idOperacion}
            idCuenta={idCuenta}
            idCuentaDetalle={entity.id}
            importeTipo='concertacion'
            refetch={refetch}
          />
        )
      }
    },
    {
      label: 'DVP',
      value: OperacionCorporateCuentaFields.ImporteDPV,
      width: '20%',
      onRenderCell: (entity: CuentaCorporateDetalle) => {
        return (
          <ImporteField
            importe={entity.importe_DPV}
            permiteEditar={entity.permiteModificar && permiteEditar}
            codMoneda={codMoneda}
            simboloMoneda={simboloMoneda}
            idOperacion={idOperacion}
            idCuenta={idCuenta}
            idCuentaDetalle={entity.id}
            importeTipo='dpv'
            refetch={refetch}
          />
        )
      }
    },
    {
      label: 'FOP',
      value: OperacionCorporateCuentaFields.ImporteFOP,
      width: '20%',
      onRenderCell: (entity: CuentaCorporateDetalle) => {
        return (
          <ImporteField
            importe={entity.importe_FOP}
            permiteEditar={entity.permiteModificar && permiteEditar}
            codMoneda={codMoneda}
            simboloMoneda={simboloMoneda}
            idOperacion={idOperacion}
            idCuenta={idCuenta}
            idCuentaDetalle={entity.id}
            importeTipo='fop'
            refetch={refetch}
          />
        )
      }
    }
  ]

  return columnsTablaCorporateCuenta
}


type CorporateCuentaProps = {
  idOperacion: number
  idPais: number
  cuenta: CuentaCorporate
  refetch: () => void
  permiteEliminar: boolean
  permiteEditar: boolean
}

/**
 * Card con una sola cuenta.
 */
const CorporateCuenta = ({
                             idOperacion,
                             idPais,
                             cuenta: detalle,
                             refetch,
                             permiteEditar,
                             permiteEliminar
                           }: CorporateCuentaProps) => {

  const {askConfirmation} = useConfirmationDialog();

  return (
    <Card>
      <Box display='flex' justifyContent='flex-end'>
        {
          permiteEliminar &&
            <Tooltip title='Eliminar'>
                <IconButton
                    onClick={
                      () => askConfirmation(
                        () => {
                          const promise = HttpCorporate.deleteCuenta(idOperacion, detalle[EntityWithIdFields.Id])

                          toast.promise(promise, {
                            success: 'Eliminado',
                            error: 'No se pudo eliminar',
                            loading: 'Eliminando'
                          }).then(() => refetch())
                        }
                      )
                    }
                >
                    <DeleteIcon color='error'/>
                </IconButton>
            </Tooltip>
        }
      </Box>
      <CardContent>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant='body2'>Número de cuenta</Typography>
              <Typography variant='body1'>{detalle[OperacionCorporateCuentaFields.NroCuenta]}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='body2'>Razón social</Typography>
              <Typography variant='body1'>{detalle[OperacionCorporateCuentaFields.RazonSocial]}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='body2'>Moneda</Typography>
              <Typography variant='body1'>{detalle[OperacionCorporateCuentaFields.DescMoneda]}</Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
      <Box>
        <TableList
          entityList={detalle[OperacionCorporateCuentaFields.Detalles]}
          columns={getColumnasParaUnaCuenta(idOperacion, permiteEditar, detalle.id, detalle.codMoneda, detalle.simboloMoneda, refetch)}
          isLoading={false}
          error={false}
        />
      </Box>
    </Card>
  )
}

type SeccionCorporateCuentasType = {
  idOperacion: number
  idPais: number
  permiteEditar: boolean
  permiteEliminar: boolean
}

/**
 * Sección con todas las cuentas.
 */
const SeccionCorporateCuentas = ({
                                     idOperacion,
                                     idPais,
                                     permiteEditar,
                                     permiteEliminar
                                   }: SeccionCorporateCuentasType) => {

  const {cuentas, refetch, status} = useCuentas(idOperacion);

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  return (
    <CardActions
      title={"Cuentas"}
      actions={
        permiteEditar &&
          <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
              Nuevo
          </Button>
      }
      colapsable
    >
      {
        status === QueryStatus.Loading ? <CircularProgress disableShrink sx={{mt: 6}}/> :
          status === QueryStatus.Error ? <Alert severity="error">Se produjo un error al cargar las cuentas.</Alert> :
            status === QueryStatus.Success ? (
              !cuentas?.length ? <Alert severity="info">No se encontraron cuentas para esta operación.</Alert> :
                <Stack spacing={4}>
                  {
                    cuentas.map((detalle, index) => (
                      <CorporateCuenta
                        key={index}
                        idOperacion={idOperacion}
                        idPais={idPais}
                        cuenta={detalle}
                        refetch={refetch}
                        permiteEliminar={permiteEliminar}
                        permiteEditar={permiteEditar}
                      />
                    ))
                  }
                </Stack>
            ) : null

      }
      {
        openDialog &&
          <DialogNuevoDetalle
              open={openDialog}
              setOpen={setOpenDialog}
              idOperacion={idOperacion}
              idPais={idPais}
              refetch={refetch}
          />
      }
    </CardActions>
  )
}

export default SeccionCorporateCuentas;
